import { createStore, persist, readJson } from "./store";
import { API_BASE, decisions, listByVerdict, replaceDecisions, settings, toast, haptic } from "./app";
import { dbGranted, deleteMine, isAccessError, pageRoom, readAll, readMine, watchPeople, writeMine, type PageRoom } from "./pageroom";
import type { CountryId, Decision, SexPref } from "../data/types";
import { count } from "./telemetry";

/** In the claude.ai preview the page itself is the room (see pageroom.ts); elsewhere, pair codes. */
const PAGE_MODE = __PREVIEW__;
export const PAGE_CODE = "PAGE";

export interface Member {
  id: string;
  name: string;
  likes: string[];
  loves: string[];
  nopes: string[];
  updatedAt: number;
  joinedAt: number;
}

export interface PartnerState {
  code: string | null;
  memberId: string | null;
  token: string | null;
  members: Member[];
  status: "idle" | "syncing" | "ok" | "offline" | "error";
  error: string | null;
  lastSync: number;
  /** matches already celebrated, so each one is announced once */
  seen: string[];
  synced: boolean;
}

const KEY = "nameling:partner";
const persisted = readJson<Pick<PartnerState, "code" | "memberId" | "token" | "members" | "seen" | "synced">>(KEY, {
  code: null,
  memberId: null,
  token: null,
  members: [],
  seen: [],
  synced: false,
});

export const partner = createStore<PartnerState>({
  ...persisted,
  status: "idle",
  error: null,
  lastSync: 0,
});

persist(partner, KEY, { toSaved: ({ code, memberId, token, members, seen, synced }) => ({ code, memberId, token, members, seen, synced }) });

/** The other people in the room, as sets for quick lookups. */
export interface PartnerSets {
  others: Member[];
  liked: Set<string>;
  loved: Set<string>;
  nopes: Set<string>;
}

function computeSets(s: PartnerState): PartnerSets {
  const others = s.members.filter((m) => m.id !== s.memberId);
  const liked = new Set<string>();
  const loved = new Set<string>();
  const nopes = new Set<string>();
  for (const m of others) {
    m.likes.forEach((k) => liked.add(k));
    m.loves.forEach((k) => {
      liked.add(k);
      loved.add(k);
    });
    m.nopes.forEach((k) => nopes.add(k));
  }
  return { others, liked, loved, nopes };
}

export const partnerSets = createStore<PartnerSets>(computeSets(partner.get()));
let lastMembers = partner.get().members;
let lastMe = partner.get().memberId;
partner.subscribe(() => {
  const s = partner.get();
  if (s.members !== lastMembers || s.memberId !== lastMe) {
    lastMembers = s.members;
    lastMe = s.memberId;
    partnerSets.set(computeSets(s));
  }
});

export function isPaired(): boolean {
  return !!partner.get().code;
}

export function partnerName(): string {
  const others = partnerSets.get().others;
  return others.length ? others.map((m) => m.name || "Your partner").join(" & ") : "Your partner";
}

export function myLikedKeys(map = decisions.get().map): string[] {
  return listByVerdict(map, [1, 2]);
}

export function matchesOf(map = decisions.get().map, sets = partnerSets.get()): string[] {
  if (!sets.others.length) return [];
  return myLikedKeys(map).filter((k) => sets.liked.has(k));
}

// ------------------------------------------------------------------ api

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const s = partner.get();
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (s.memberId && s.token) headers.authorization = `Bearer ${s.memberId}.${s.token}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api${path}`, { ...init, headers: { ...headers, ...(init.headers as object) } });
  } catch {
    throw new ApiError(0, "You seem to be offline.");
  }
  if (!res.ok) {
    let msg = `Something went wrong (${res.status}).`;
    try {
      msg = (await res.json()).error || msg;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

interface RoomResponse {
  code: string;
  member?: { id: string; token: string };
  members: Member[];
  /** the room's version, sent back on the next read so an unchanged room costs almost nothing */
  v?: string;
}

/** The version of the room as last read in full, so the next read can skip what hasn't moved. */
let roomV: { code: string; v: string } | null = null;

/** the form the server keeps name keys in (rooms-core.ts) */
const KEY_RE = /^[a-z'\-]{1,40}$/;

function payload() {
  const map = decisions.get().map;
  const s = settings.get();
  const keys = (v: Decision["d"][]) => listByVerdict(map, v).filter((k) => KEY_RE.test(k));
  return {
    name: s.myName.trim().slice(0, 40),
    // so a partner who joins starts on the same country's names, for a girl or a boy
    country: s.country,
    sex: s.sex,
    likes: keys([1]),
    loves: keys([2]),
    // newest first: if there are ever too many to keep, the server leaves off the oldest
    nopes: keys([-1]),
  };
}

function adopt(r: RoomResponse): void {
  roomV = r.v ? { code: r.code, v: r.v } : null;
  partner.set((s) => ({
    code: r.code,
    memberId: r.member?.id ?? s.memberId,
    token: r.member?.token ?? s.token,
    members: r.members,
    status: "ok",
    error: null,
    lastSync: Date.now(),
  }));
}

export async function createRoom(): Promise<void> {
  if (PAGE_MODE) return joinPage();
  partner.set({ status: "syncing", error: null });
  try {
    const r = await api<RoomResponse>("/rooms", { method: "POST", body: JSON.stringify(payload()) });
    adopt(r);
    count("pair.create");
    partner.set({ seen: [], synced: true });
    startSync();
    // a new pairing starts with the first few thousand swipes; the rest follow now
    schedulePush(0);
  } catch (e) {
    partner.set({ status: "error", error: (e as Error).message });
    throw e;
  }
}

export interface RoomPeek {
  names: string[];
  full: boolean;
  /** what the person who made the pairing is choosing (absent for pairings made by older apps) */
  country?: CountryId;
  sex?: SexPref;
}

/** First names of the people in a room, for a friendlier invite. */
export async function peekRoom(code: string): Promise<RoomPeek | null> {
  try {
    const res = await fetch(`${API_BASE}/api/rooms/${encodeURIComponent(code.toUpperCase().replace(/[^A-Z0-9]/g, ""))}/peek`);
    return res.ok ? ((await res.json()) as RoomPeek) : null;
  } catch {
    return null;
  }
}

export async function joinRoom(code: string): Promise<void> {
  const clean = code.toUpperCase().replace(/[^A-Z0-9]/g, "");
  partner.set({ status: "syncing", error: null });
  try {
    const r = await api<RoomResponse>(`/rooms/${clean}/join`, { method: "POST", body: JSON.stringify(payload()) });
    adopt(r);
    count("pair.join");
    // matches that already exist on joining get one combined announcement, not a parade
    const existing = matchesOf();
    partner.set({ seen: existing, synced: true });
    if (existing.length) toast(`You already agree on ${existing.length} name${existing.length > 1 ? "s" : ""}`);
    startSync();
    schedulePush(0);
  } catch (e) {
    partner.set({ status: "error", error: (e as Error).message });
    throw e;
  }
}

let pushTimer = 0;
let pushing: Promise<void> | null = null;
let dirty = false;

async function push(): Promise<void> {
  const s = partner.get();
  if (!s.code || !s.memberId) return;
  dirty = false;
  if (PAGE_MODE) return pushPage();
  try {
    await api(`/rooms/${s.code}/members/${s.memberId}`, { method: "PUT", body: JSON.stringify(payload()) });
    partner.set({ status: "ok", error: null, lastSync: Date.now() });
  } catch (e) {
    const status = (e as ApiError).status;
    // try again later when offline or the server had trouble; a save it refused won't do better
    dirty = status === 0 || status === 429 || status >= 500;
    handleError(e as ApiError);
  }
}

/** One save at a time, in order: a slow older save can't land after a newer one. */
function queuePush(): Promise<void> {
  const run = (pushing ?? Promise.resolve()).then(() => push());
  const mine = run.finally(() => {
    if (pushing === mine) pushing = null;
  });
  pushing = mine;
  return mine;
}

function schedulePush(delay = 1500): void {
  if (!isPaired()) return;
  dirty = true;
  clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => void queuePush(), delay);
}

/** Fetch your partner's latest swipes. True if anything in the room had changed. */
export async function pull(): Promise<boolean> {
  const s = partner.get();
  if (!s.code) return false;
  if (pushing) await pushing;
  if (PAGE_MODE) {
    await pullPage();
    return true;
  }
  try {
    const since = roomV?.code === s.code ? `?v=${encodeURIComponent(roomV.v)}` : "";
    const r = await api<RoomResponse & { unchanged?: boolean }>(`/rooms/${s.code}${since}`);
    // unpaired (or paired again) while this was on its way: it's no longer ours
    if (partner.get().code !== s.code || partner.get().memberId !== s.memberId) return false;
    if (dirty) schedulePush(0);
    if (r.unchanged) {
      partner.set({ status: "ok", error: null, lastSync: Date.now() });
      return false;
    }
    adopt(r);
    announceNewMatches();
    return true;
  } catch (e) {
    if (partner.get().code !== s.code) return false;
    handleError(e as ApiError);
    return false;
  }
}

function handleError(e: ApiError): void {
  if (e.status === 0) partner.set({ status: "offline", error: e.message });
  else if (e.status === 401 || e.status === 404)
    partner.set({ status: "error", error: "This pairing has ended. Unpair here to start a new one." });
  else partner.set({ status: "error", error: e.message });
}

/** the first look at the room since the app opened: matches it finds were made while it was closed */
let firstLook = true;

/** Matches that appeared because your partner liked something you had already liked. */
function announceNewMatches(): void {
  const all = matchesOf();
  const seen = new Set(partner.get().seen);
  const fresh = all.filter((k) => !seen.has(k));
  const away = firstLook;
  firstLook = false;
  if (!fresh.length) return;
  partner.set({ seen: [...seen, ...fresh] });
  count("match", fresh.length);
  haptic([20, 60, 20]);
  // the app says which names (it knows them); without it, just how many
  if (newMatchListeners.size) newMatchListeners.forEach((l) => l(fresh, away));
  else {
    const who = partnerName();
    toast(fresh.length === 1 ? `New match: ${who} liked one of yours too` : `${fresh.length} new matches with ${who}`);
  }
}

const newMatchListeners = new Set<(keys: string[], whileAway: boolean) => void>();
/** Told about matches that arrive from your partner's phone (and whether they came while away). */
export function onNewMatches(l: (keys: string[], whileAway: boolean) => void): () => void {
  newMatchListeners.add(l);
  return () => newMatchListeners.delete(l);
}

/** Call when the user likes a name: true if it's a match to celebrate now. */
export function checkInstantMatch(key: string): boolean {
  if (!partnerSets.get().liked.has(key)) return false;
  const seen = partner.get().seen;
  if (seen.includes(key)) return false;
  partner.set({ seen: [...seen, key] });
  count("match");
  return true;
}

/** Unpair: the server deletes the whole pairing. Refused (throws) when the server can't be
 * reached, so the pairing isn't forgotten here while its lists stay on the server. */
export async function leaveRoom(): Promise<void> {
  const s = partner.get();
  if (PAGE_MODE) await leavePage();
  else if (s.code && s.memberId) {
    try {
      await api(`/rooms/${s.code}/members/${s.memberId}`, { method: "DELETE" });
    } catch (e) {
      // only 401 or 404 mean it's gone already (your partner unpaired first); anything else (offline,
      // busy, a wifi sign-in page) and the pairing would stay on the server, so keep it here too
      const status = (e as ApiError).status;
      if (status !== 401 && status !== 404)
        throw new Error("Couldn't reach Nameling to delete the pairing. Try again when you're online.");
    }
  }
  stopSync();
  roomV = null;
  partner.set({ code: null, memberId: null, token: null, members: [], seen: [], status: "idle", error: null, synced: false });
}

// ------------------------------------------------------------------ restore codes

export function restoreCode(): string | null {
  const s = partner.get();
  if (PAGE_MODE || !s.code || !s.memberId || !s.token) return null;
  return `NML1.${s.code}.${s.memberId}.${s.token}`;
}

export async function restoreFrom(code: string): Promise<number> {
  const m = /^NML1\.([A-Z0-9]+)\.([a-z0-9]+)\.([a-f0-9]+)$/.exec(code.trim());
  if (!m) throw new Error("That doesn't look like a Nameling restore code.");
  partner.set({ code: m[1], memberId: m[2], token: m[3], members: [], status: "syncing", error: null });
  const r = await api<RoomResponse>(`/rooms/${m[1]}`).catch((e: ApiError) => {
    partner.set({ code: null, memberId: null, token: null, status: "error", error: e.message });
    throw e;
  });
  adopt(r);
  // bring back this member's own swipes, keeping anything decided on this device
  const me = r.members.find((x) => x.id === m[2]);
  let restored = 0;
  if (me) {
    const map: Record<string, Decision> = { ...decisions.get().map };
    const add = (keys: string[], d: Decision["d"]) =>
      keys.forEach((k, i) => {
        if (!map[k]) {
          map[k] = { d, t: me.updatedAt - i };
          restored++;
        }
      });
    add(me.loves, 2);
    add(me.likes, 1);
    add(me.nopes, -1);
    replaceDecisions(map);
    if (me.name && !settings.get().myName) settings.set({ myName: me.name });
  }
  partner.set({ seen: matchesOf(), synced: true });
  startSync();
  return restored;
}

// ------------------------------------------------------------------ sync loop

/**
 * How often to look for your partner's swipes: every 15 seconds while either of you is busy,
 * easing off to every 2 minutes while nothing changes, and never while the app is in the
 * background. Thousands of open phones then cost the server little.
 */
const POLL_MIN = 15_000;
const POLL_MAX = 120_000;
let pollDelay = POLL_MIN;
let pollTimer = 0;
let nextPollAt = 0;
let started = false;

function schedulePoll(delay: number): void {
  clearTimeout(pollTimer);
  nextPollAt = Date.now() + delay;
  pollTimer = window.setTimeout(async () => {
    if (document.visibilityState === "visible" && isPaired()) {
      const changed = await pull();
      pollDelay = changed ? POLL_MIN : Math.min(POLL_MAX, pollDelay * 2);
    }
    if (isPaired()) schedulePoll(pollDelay);
  }, delay);
}

/** Back to frequent checks, without putting off one that's already due sooner. */
export function syncSoon(): void {
  pollDelay = POLL_MIN;
  if (started && isPaired() && nextPollAt - Date.now() > POLL_MIN) schedulePoll(POLL_MIN);
}

function onVisible(): void {
  if (document.visibilityState === "visible") {
    pollDelay = POLL_MIN;
    if (isPaired()) pull().finally(() => schedulePoll(POLL_MIN));
  } else if (dirty) {
    clearTimeout(pushTimer);
    void queuePush();
  }
}

export function startSync(): void {
  if (PAGE_MODE) {
    initPage();
    return;
  }
  if (!isPaired()) return;
  if (!started) {
    started = true;
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", () => pull());
  }
  pollDelay = POLL_MIN;
  pull().finally(() => schedulePoll(POLL_MIN));
}

function stopSync(): void {
  clearTimeout(pollTimer);
  clearTimeout(pushTimer);
  nextPollAt = 0;
}

let lastRev = decisions.get().rev;
decisions.subscribe(() => {
  const rev = decisions.get().rev;
  if (rev !== lastRev) {
    lastRev = rev;
    schedulePush();
    syncSoon();
  }
});
// your partner sees your name and what you're choosing, so those are worth syncing; other
// settings stay local
let lastShared = "";
const shared = () => {
  const s = settings.get();
  return `${s.myName}|${s.country}|${s.sex}`;
};
lastShared = shared();
settings.subscribe(() => {
  const now = shared();
  if (now !== lastShared) {
    lastShared = now;
    if (isPaired()) schedulePush(3000);
  }
});

// ------------------------------------------------------------------ page room (claude.ai preview)

export type PageAccess = "checking" | "ready" | "readonly" | "unavailable";
/** whether this view of the page can join in: shown on the Together tab before anyone joins */
export const pageAccess = createStore<{ access: PageAccess }>({ access: PAGE_MODE ? "checking" : "unavailable" });

const READ_ONLY = "You can see this page but not save to it. Ask whoever shared it with you for edit access.";
let unwatch: (() => void) | null = null;
/** after joining, the first snapshot greets existing matches together instead of one by one */
let greetOnNext = false;

function pageBody() {
  const s = partner.get();
  const mine = s.members.find((m) => m.id === s.memberId);
  return { ...payload(), joinedAt: mine?.joinedAt || Date.now() };
}

/** Bring back swipes saved on this page from another device, keeping anything decided here. */
function mergeRemote(me: Member): number {
  const map: Record<string, Decision> = { ...decisions.get().map };
  let added = 0;
  const add = (keys: string[], d: Decision["d"]) =>
    keys.forEach((k, i) => {
      if (!map[k]) {
        map[k] = { d, t: me.updatedAt - i };
        added++;
      }
    });
  add(me.loves, 2);
  add(me.likes, 1);
  add(me.nopes, -1);
  if (added) replaceDecisions(map);
  if (me.name && !settings.get().myName) settings.set({ myName: me.name });
  return added;
}

/** does the saved copy of my swipes differ from this device's? */
function differs(remote: Member | undefined): boolean {
  if (!remote) return true;
  const p = payload();
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((k, i) => k === b[i]);
  return remote.name !== p.name || !same(remote.likes, p.likes) || !same(remote.loves, p.loves) || !same(remote.nopes, p.nopes);
}

function watch(room: PageRoom): void {
  if (unwatch) return;
  let first = true;
  unwatch = watchPeople(
    room,
    (members) => {
      partner.set({ members, status: "ok", error: null, lastSync: Date.now() });
      if (greetOnNext) {
        greetOnNext = false;
        const existing = matchesOf();
        partner.set({ seen: existing });
        if (existing.length) toast(`You already agree on ${existing.length} name${existing.length > 1 ? "s" : ""}!`);
      } else announceNewMatches();
      // catch up on anything decided while the page couldn't save (once per visit)
      if (first) {
        first = false;
        if (differs(members.find((m) => m.id === room.me))) schedulePush(0);
      }
    },
    (e) => {
      unwatch = null;
      partner.set(
        e.code === "revoked"
          ? { status: "error", error: "This page's shared data is no longer available to you." }
          : { status: "offline", error: "Lost touch with the page. Changes will sync when it's back." },
      );
    },
  );
}

async function joinPage(): Promise<void> {
  partner.set({ status: "syncing", error: null });
  const room = await pageRoom();
  if (!room) {
    partner.set({ status: "error", error: "Matching only works when this page is opened on claude.ai, signed in." });
    throw new Error("page room unavailable");
  }
  try {
    const mine = await readMine(room);
    const restored = mine ? mergeRemote(mine) : 0;
    partner.set({ code: PAGE_CODE, memberId: room.me, token: null, members: mine ? [mine] : [], seen: [], synced: true });
    await writeMine(room, pageBody());
    if (restored) toast(`Brought back ${restored} swipe${restored === 1 ? "" : "s"} from your other device`);
  } catch (e) {
    const denied = isAccessError(e);
    if (denied) pageAccess.set({ access: "readonly" });
    partner.set({
      code: null,
      memberId: null,
      members: [],
      status: "error",
      error: denied ? READ_ONLY : "Couldn't join in just now. Please try again.",
    });
    throw e;
  }
  greetOnNext = true;
  partner.set({ status: "ok", lastSync: Date.now() });
  watch(room);
}

async function pushPage(): Promise<void> {
  const room = await pageRoom();
  if (!room) {
    dirty = true;
    return;
  }
  try {
    await writeMine(room, pageBody());
    partner.set({ status: "ok", error: null, lastSync: Date.now() });
  } catch (e) {
    dirty = true;
    partner.set(isAccessError(e) ? { status: "error", error: READ_ONLY } : { status: "offline", error: "Couldn't save to the page just now." });
  }
}

async function pullPage(): Promise<void> {
  const room = await pageRoom();
  if (!room) return;
  try {
    partner.set({ members: await readAll(room), status: "ok", error: null, lastSync: Date.now() });
    announceNewMatches();
    if (!unwatch) watch(room);
    if (dirty) schedulePush(0);
  } catch {
    partner.set({ status: "offline", error: "Couldn't reach the page's shared data." });
  }
}

async function leavePage(): Promise<void> {
  unwatch?.();
  unwatch = null;
  const room = await pageRoom();
  if (room) await deleteMine(room).catch(() => {});
}

async function initPage(): Promise<void> {
  const room = await pageRoom();
  pageAccess.set({ access: !room ? "unavailable" : room.canWrite === false ? "readonly" : "ready" });
  if (!room) {
    if (partner.get().code) partner.set({ status: "offline", error: "Can't reach this page's shared data here." });
    return;
  }
  const s = partner.get();
  // someone else is signed in on this browser now: their pairing isn't yours
  if (s.code && s.memberId !== room.me)
    partner.set({ code: null, memberId: null, token: null, members: [], seen: [], synced: false, status: "idle", error: null });
  if (!partner.get().code) {
    // joined in from another device? then this one is in too (asking only if the page may already read)
    if (!(await dbGranted())) return;
    const mine = await readMine(room).catch(() => null);
    if (!mine) return;
    const restored = mergeRemote(mine);
    partner.set({ code: PAGE_CODE, memberId: room.me, token: null, members: [mine], seen: [], synced: true });
    greetOnNext = true;
    if (restored) toast(`Brought back ${restored} swipe${restored === 1 ? "" : "s"} from your other device`);
  }
  if (!started) {
    started = true;
    document.addEventListener("visibilitychange", onVisible);
  }
  watch(room);
}
